import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Box, Button, Chip, IconButton, Menu, MenuItem, TableCell, TableRow, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useRef, useState } from "react";
import { useModals } from "../../context/ModalContext";
import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { formatUSPhone, getRecordEmail } from "../../utils";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { colors, alphaVar } from "../ui/colors";
import { useDialog } from "../ui/dialogs/useDialog";
import { useVendors } from "./hooks/useVendors";

const GRAY = colors.gray;
const ORANGE = colors.orange;

interface VendorsRowProps {
  row: {
    uid: string;
    firstName?: string;
    lastName?: string;
    vendorCompanyName?: string;
    alias?: string;
    type?: string;
    status?: string;
    emails?: any[];
    phoneNumbers?: any[];
    [key: string]: any;
  };
  handleDelete: (uid: string) => Promise<void> | void;
  handleUpdate: () => Promise<void> | void;
  disableMenuOptions?: boolean;
  contact?: any;
  onLinkContact?: (vendor: any) => void | Promise<any>;
  activeTable?: string;
  onOpenModalDetails?: (vendor: any) => void;
}

export const VendorsRow: React.FC<VendorsRowProps> = ({
  row,
  handleDelete,
  handleUpdate,
  disableMenuOptions = false,
  contact,
  onLinkContact,
  activeTable = "vendors",
  onOpenModalDetails,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { showDialog, DialogComponent } = useDialog();
  const { closeByKey } = useModals();
  const { showSnackbar } = useGlobalSnackbar();
  const { editVendor } = useVendors();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  const linkContactTriggerRef = useRef<HTMLButtonElement | null>(null);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const closeMenu = () => setMenuAnchor(null);

  const handleConvertStatus = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    closeMenu();
    const nextStatus = row.status === "PROSPECT" ? "ACTIVE" : "PROSPECT";
    try {
      const res = await editVendor(row.uid, { status: nextStatus });
      if (res?.ok) {
        showSnackbar(nextStatus === "ACTIVE" ? "Converted to active vendor successfully" : "Converted to prospect successfully", "success");
        await handleUpdate();
      } else {
        showSnackbar("Failed to convert vendor status", "error");
      }
    } catch (err: any) {
      showSnackbar(err?.message || "Failed to convert vendor status", "error");
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "ACTIVE":
        return { bg: "#f0fdf4", text: "#16a34a", border: "#bbf7d0" };
      case "PROSPECT":
        return { bg: "#eff6ff", text: "#2563eb", border: "#bfdbfe" };
      case "PENDING":
        return { bg: "#fffbeb", text: "#d97706", border: "#fde68a" };
      case "SUSPENDED":
        return { bg: "#fef2f2", text: "#dc2626", border: "#fecaca" };
      case "INACTIVE":
      default:
        return { bg: "#f3f4f6", text: "#4b5563", border: "var(--fx-surface-border, #E2E8F0)" };
    }
  };

  const contactName = [row.alias, row.firstName, row.lastName].filter(Boolean).join(" ").trim();
  const vendorName = row.type === "CORPORATE" ? row.vendorCompanyName || contactName || "Not Provided" : contactName || "Not Provided";

  const handleLinkContact = () => {
    if (!contact || !contact.phoneNumbers || contact.phoneNumbers.length === 0) {
      return;
    }
    linkContactTriggerRef.current?.click();
  };

  const defaultEmail = getRecordEmail(row) || "Not Provided";

  const defaultPhone =
    Array.isArray(row?.phoneNumbers) && row?.phoneNumbers.length > 0
      ? row?.phoneNumbers.find((p: any) => p.isDefault || p.isPrimary) || row?.phoneNumbers[0]
      : row?.phones && row?.phones.length > 0
        ? row?.phones.find((p: any) => p.isDefault) || row?.phones[0]
        : null;

  const phoneValue = defaultPhone?.phone || defaultPhone?.number || row?.phone || "";
  const formattedPhone = phoneValue ? formatUSPhone(phoneValue) : "Not Provided";

  const statusInfo = getStatusColor(row.status);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenu();
    onOpenModalDetails?.(row);
  };

  const handleRowClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-ignore-row-click="true"]')) return;
    onOpenModalDetails?.(row);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenu();
    showDialog({
      type: "confirm",
      title: "Delete Vendor",
      message: `Are you sure you want to delete vendor "${vendorName}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await handleDelete(row.uid);
        } catch (err: any) {
          console.error("Failed to delete vendor", err);
        }
      },
    });
  };

  return (
    <>
      {DialogComponent}

      <TableRow
        hover
        onClick={handleRowClick}
        sx={{
          cursor: "pointer",
          "& td": {
            py: 1.5,
            px: 2,
            borderBottom: `1px solid var(--fx-surface-border, #E2E8F0)`,
            fontSize: "0.875rem",
          },
        }}
      >
        <TableCell>
          <Typography sx={{ fontWeight: 600, color: "var(--fx-surface-text, #0F172A)" }}>{vendorName}</Typography>
          {row.type === "CORPORATE" && contactName && (
            <Typography variant="caption" color="textSecondary">
              Contact: {contactName}
            </Typography>
          )}
        </TableCell>

        <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
          <Chip
            label={row.type || "INDEPENDENT"}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.75rem",
              textTransform: "capitalize",
              backgroundColor: row.type === "CORPORATE" ? "var(--fx-colors-info-50, #EFF6FF)" : "var(--fx-surface-base, #FFFFFF)",
              color: row.type === "CORPORATE" ? "#1d4ed8" : "#6d28d9",
            }}
          />
        </TableCell>

        <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
          <Chip
            label={row.status || "ACTIVE"}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              backgroundColor: statusInfo.bg,
              color: statusInfo.text,
              border: `1px solid ${statusInfo.border}`,
            }}
          />
        </TableCell>

        <TableCell>
          <Typography variant="body2" sx={{ color: "var(--fx-surface-textsecondary, #64748B)" }}>
            {defaultEmail}
          </Typography>
        </TableCell>

        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
          <Typography variant="body2" sx={{ color: "var(--fx-surface-textsecondary, #64748B)" }}>
            {formattedPhone}
          </Typography>
        </TableCell>

        {contact && onLinkContact && (
          <TableCell align="right" sx={{ width: 100 }} data-ignore-row-click={true} onClick={(e) => e.stopPropagation()}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={handleLinkContact}
              sx={{
                textTransform: "none",
                bgcolor: ORANGE.base,
                color: "white",
                fontWeight: 600,
                fontSize: "0.75rem",
                px: 2,
                py: 0.5,
                borderRadius: 1.5,
                "&:hover": { bgcolor: ORANGE[600] },
              }}
            >
              Link
            </Button>
          </TableCell>
        )}

        {activeTable === "prospects" && (
          <TableCell align="right" sx={{ width: 160 }} data-ignore-row-click={true} onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleConvertStatus}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                borderColor: ORANGE.base,
                color: ORANGE.base,
                "&:hover": {
                  bgcolor: alphaVar(ORANGE.base, 0.08),
                  borderColor: ORANGE[600],
                },
              }}
            >
              Convert to Vendor
            </Button>
          </TableCell>
        )}

        {!disableMenuOptions && (
          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
            <IconButton size="small" onClick={openMenu}>
              <MoreHorizIcon sx={{ color: GRAY[500] }} />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={menuOpen}
              onClose={closeMenu}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              PaperProps={{
                sx: {
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                  border: "1px solid var(--fx-surface-border, #E2E8F0)",
                  minWidth: 160,
                },
              }}
            >
              <MenuItem onClick={handleEditClick} sx={{ fontSize: "0.875rem", py: 1 }}>
                <EditIcon sx={{ mr: 1, fontSize: 18, color: "var(--fx-surface-textsecondary, #64748B)" }} />
                Edit
              </MenuItem>
              <MenuItem onClick={handleDeleteClick} sx={{ fontSize: "0.875rem", py: 1, color: "#dc2626" }}>
                <DeleteIcon sx={{ mr: 1, fontSize: 18, color: "#dc2626" }} />
                Delete
              </MenuItem>
            </Menu>
          </TableCell>
        )}
      </TableRow>

      {contact && onLinkContact && (
        <BaseModalComponent
          modalKey={`link-contact-${row.uid}`}
          modalLabel="Link Phone Number"
          defaultSizes={{ width: 450, height: "auto" }}
          elementTrigger={
            <button
              ref={linkContactTriggerRef}
              type="button"
              style={{ position: "absolute", left: -99999, top: -99999 }}
              onClick={(e) => e.stopPropagation()}
            />
          }
        >
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography fontSize="0.875rem" sx={{ color: GRAY[700], lineHeight: 1.5 }}>
              Are you sure you want to add phone number{" "}
              <strong>{contact?.phoneNumbers?.[0]?.number ? formatUSPhone(contact.phoneNumbers[0].number) : ""}</strong> to vendor{" "}
              <strong>{vendorName}</strong>?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => closeByKey(`link-contact-${row.uid}`)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  borderColor: GRAY[300],
                  color: GRAY[700],
                  "&:hover": {
                    borderColor: GRAY[400],
                    backgroundColor: alphaVar(GRAY[400], 0.05),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disableElevation
                size="small"
                onClick={async () => {
                  try {
                    await onLinkContact?.({
                      vendorUid: row.uid,
                      phoneNumbers: row.phoneNumbers,
                    });
                  } catch (error: any) {
                    console.error("Failed to link phone number", error);
                  } finally {
                    closeByKey(`link-contact-${row.uid}`);
                  }
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  bgcolor: ORANGE.base,
                  color: "#fff",
                  "&:hover": {
                    bgcolor: ORANGE[600],
                  },
                }}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </BaseModalComponent>
      )}
    </>
  );
};
